import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { TechFormSectionLoadError } from "../components/TechFormSectionLoadError";
import type { TechFormTablePageProps } from "../models/tech-form-page.models";
import { TECH_FORM_ENDPOINTS } from "../models/tech-form-section-data.constants";
import { TECH_FORM_GENSET_COUNTS } from "../models/tech-form-validation-ui.constants";
import { TECH_FORM_CACHE_MODES } from "../models/tech-form-vessel-cache.constants";
import { mapSqlToTq2DesignDetailsForm } from "../models/tq2-design-details.models";
import {
  PowerManagementItem,
  Tq8PowerManagementM,
  mapSqlToTq8PowerManagementForm,
  mapSqlToTq8PowerManagementTable,
  tq8PowerManagementFormInit,
} from "../models/tq8-power-management.models";
import { isTechFormSectionDataCached, loadTechFormEndpointData } from "../services/tech-form-section-data.service";
import {
  tq1VesselDetailsAtom,
  tq2DesignDetailsAtom,
  tq8PowerManagementAtom,
  tq8PowerManagementTableAtom,
} from "../state/tech-form.atoms";
import { ReactSelectStyling } from "../utils/TechUtils";
import { preparePowerRowsForEditing } from "../utils/prepare-power-rows-for-editing.utils";
import {
  PowerManagementTableErrors,
  sanitizeTechFormInputForField,
  validatePowerManagementTable,
  validateTq8PowerManagement,
} from "../utils/tech-form-validations";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Select from "react-select";
import { twMerge } from "tailwind-merge";

export const Tq8PowerManagement: React.FC<TechFormTablePageProps> = ({
  isCompletedForm,
  cacheInitialForm,
  onSectionReady,
}) => {
  const [designDetails, setDesignDetails] = useAtom(tq2DesignDetailsAtom);
  const [vesselDetails, setVesselDetails] = useAtom(tq1VesselDetailsAtom);
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(tq8PowerManagementAtom);
  const [storedTable, setStoredTable] = useAtom(tq8PowerManagementTableAtom);

  const { imoNumber, vesselName } = useParams();
  const initialFormData: Tq8PowerManagementM = tq8PowerManagementFormInit;
  const [isLoading, setIsLoading] = useState(false);
  const [isSchemaMetadataLoading, setIsSchemaMetadataLoading] = useState(
    vesselDetails?.techFormSchemaVersion === undefined,
  );
  const [isSectionDataReady, setIsSectionDataReady] = useState(false);
  const [sectionLoadError, setSectionLoadError] = useState(false);
  const [sectionLoadAttempt, setSectionLoadAttempt] = useState(0);
  const [form, setForm] = useState<Tq8PowerManagementM>(initialFormData);

  const [table, setTable] = useState<PowerManagementItem[]>(() => {
    return preparePowerRowsForEditing(storedTable);
  });

  const [formErrors, setFormErrors] = useState<Partial<Record<keyof Tq8PowerManagementM, string>>>({});
  const [tableErrors, setTableErrors] = useState<PowerManagementTableErrors>({});
  const scrubberFitted = String(designDetails?.scrubberFitted ?? "")
    .trim()
    .toLowerCase();
  const scrubberStatusIsResolved = ["yes", "no"].includes(scrubberFitted);
  const shouldFetchDesignDetails = isCompletedForm || !scrubberStatusIsResolved;
  const techFormSchemaVersion = vesselDetails?.techFormSchemaVersion;
  const isSchemaMetadataReady = techFormSchemaVersion !== undefined;

  const updateTable = (id: number, name: string, value: string | number) => {
    const sanitizedValue = sanitizeTechFormInputForField(name, value);

    setTable((prevData) => {
      const nextTable = prevData.map((item) => (item.id === id ? { ...item, [name]: sanitizedValue } : item));
      setTableErrors(validatePowerManagementTable(nextTable, form, scrubberFitted, techFormSchemaVersion));
      return nextTable;
    });
  };

  useEffect(() => {
    if (!shouldFetchDesignDetails || !imoNumber || !vesselName) return;

    let ignoreResult = false;
    const resolveScrubberStatus = async () => {
      try {
        const result = await loadTechFormEndpointData<any[]>({
          imoNumber,
          vesselName,
          mode: isCompletedForm ? TECH_FORM_CACHE_MODES.completed : TECH_FORM_CACHE_MODES.editable,
          endpoint: TECH_FORM_ENDPOINTS.vesselDesign,
        });
        const fetchedDesignDetails = mapSqlToTq2DesignDetailsForm(result[0]);
        if (ignoreResult) return;

        setDesignDetails((currentDesignDetails: Record<string, unknown>) =>
          isCompletedForm
            ? fetchedDesignDetails
            : {
                ...fetchedDesignDetails,
                ...currentDesignDetails,
                scrubberFitted: fetchedDesignDetails.scrubberFitted,
              },
        );
      } catch (error: unknown) {
        if (!ignoreResult) {
          setSectionLoadError(true);
          console.error("Unable to resolve scrubber status for power management", error);
        }
      }
    };

    resolveScrubberStatus();
    return () => {
      ignoreResult = true;
    };
  }, [imoNumber, vesselName, isCompletedForm, sectionLoadAttempt, setDesignDetails, shouldFetchDesignDetails]);

  useEffect(() => {
    if (isSchemaMetadataReady || !imoNumber || !vesselName) {
      setIsSchemaMetadataLoading(false);
      return;
    }

    let ignoreResult = false;
    setIsSchemaMetadataLoading(true);
    const resolveSchemaMetadata = async () => {
      try {
        const generalInformation = await loadTechFormEndpointData<any[]>({
          imoNumber,
          vesselName,
          mode: isCompletedForm ? TECH_FORM_CACHE_MODES.completed : TECH_FORM_CACHE_MODES.editable,
          endpoint: TECH_FORM_ENDPOINTS.generalInformation,
        });
        if (ignoreResult) return;

        const resolvedSchemaVersion = generalInformation[0]?.TECH_FORM_SCHEMA_VERSION ?? null;
        setVesselDetails((currentVesselDetails: Record<string, unknown>) => ({
          ...currentVesselDetails,
          techFormSchemaVersion: resolvedSchemaVersion,
        }));
      } catch (error: unknown) {
        if (!ignoreResult) {
          setSectionLoadError(true);
          console.error("Unable to resolve Technical Form schema version for power management", error);
        }
      } finally {
        if (!ignoreResult) {
          setIsSchemaMetadataLoading(false);
        }
      }
    };

    void resolveSchemaMetadata();
    return () => {
      ignoreResult = true;
    };
  }, [imoNumber, vesselName, isCompletedForm, isSchemaMetadataReady, sectionLoadAttempt, setVesselDetails]);

  useEffect(() => {
    let ignoreResult = false;
    setSectionLoadError(false);
    const _form = storedForm ?? {};
    const _table = storedTable ?? {};
    const storageisEmpty = Object.keys(_form).length === 0 && Object.keys(_table).length === 0;

    if (isCompletedForm) {
      void fetchData(() => ignoreResult);
    } else {
      if (storageisEmpty) {
        void fetchData(() => ignoreResult);
      } else {
        setForm(_form);
        setTable(preparePowerRowsForEditing(_table));
        cacheInitialForm(_form, _table);
        setIsSectionDataReady(true);
      }
    }

    return () => {
      ignoreResult = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imoNumber, vesselName, isCompletedForm, sectionLoadAttempt]);

  useEffect(() => {
    if (isSectionDataReady && isSchemaMetadataReady) {
      onSectionReady?.();
    }
  }, [isSchemaMetadataReady, isSectionDataReady, onSectionReady]);

  useEffect(() => {
    if (!isSchemaMetadataReady) return;
    setFormErrors(validateTq8PowerManagement(form, techFormSchemaVersion));
  }, [form, isSchemaMetadataReady, techFormSchemaVersion]);

  useEffect(() => {
    if (!isSchemaMetadataReady) return;
    setTableErrors(validatePowerManagementTable(table, form, scrubberFitted, techFormSchemaVersion));
  }, [form, table, scrubberFitted, isSchemaMetadataReady, techFormSchemaVersion]);

  const fetchData = async (isStale: () => boolean) => {
    if (!imoNumber || !vesselName) return;

    try {
      const requestIdentity = {
        imoNumber,
        vesselName,
        mode: isCompletedForm ? TECH_FORM_CACHE_MODES.completed : TECH_FORM_CACHE_MODES.editable,
      };
      if (!isTechFormSectionDataCached({ ...requestIdentity, section: 8 })) {
        setIsLoading(true);
      }

      const [formResult, tableResult] = await Promise.all([
        loadTechFormEndpointData<any[]>({
          ...requestIdentity,
          endpoint: TECH_FORM_ENDPOINTS.powerManagementIndividualFields,
        }),
        loadTechFormEndpointData<any[]>({
          ...requestIdentity,
          endpoint: TECH_FORM_ENDPOINTS.powerManagement,
        }),
      ]);
      if (isStale()) return;

      const _formRes = formResult[0];
      const _form = _formRes ? mapSqlToTq8PowerManagementForm(_formRes) : { ...tq8PowerManagementFormInit };
      setForm(_form);
      setStoredForm(_form);

      const _table = mapSqlToTq8PowerManagementTable(tableResult);
      setTable(_table);
      setStoredTable(_table);
      cacheInitialForm(_form, _table);
      setIsSectionDataReady(true);
    } catch (error: unknown) {
      if (!isStale()) {
        setSectionLoadError(true);
        console.error("Unable to load Technical Form power-management data", error);
      }
    } finally {
      if (!isStale()) {
        setIsLoading(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeTechFormInputForField(name, value);

    setForm((prevState) => {
      const nextForm = {
        ...prevState,
        [name]: sanitizedValue,
      };
      setFormErrors(validateTq8PowerManagement(nextForm, techFormSchemaVersion));
      setTableErrors(validatePowerManagementTable(table, nextForm, scrubberFitted, techFormSchemaVersion));
      return nextForm;
    });
    setStoredForm({
      ...form,
      [name]: sanitizedValue,
    });
  };

  // Storage
  useEffect(() => {
    if (isSectionDataReady) {
      setStoredTable(table);
    }
  }, [isSectionDataReady, setStoredTable, table]);

  useEffect(() => {
    if (isSectionDataReady) {
      setStoredForm(form);
    }
  }, [form, isSectionDataReady, setStoredForm]);

  const options = [
    { value: "ratedPower", label: "Rated Power" },
    {
      value: "powerobtainedfromElectricalLoadBalancedrawing",
      label: "Power obtained from Electrical Load Balance drawing",
    },
  ];

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrors(validateTq8PowerManagement(form, techFormSchemaVersion));
    setTableErrors(validatePowerManagementTable(table, form, scrubberFitted, techFormSchemaVersion));
  };

  const tableInputClass = (error?: string) =>
    twMerge(
      "mt-1 w-full rounded-md border p-2 focus:outline-none",
      error ? "border-red-600" : theme.uwhcAssistant.border,
    );

  const tableError = (error?: string) => (error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null);

  const selectStyles = ReactSelectStyling();

  if (sectionLoadError) {
    return <TechFormSectionLoadError onRetry={() => setSectionLoadAttempt((attempt) => attempt + 1)} />;
  }

  return (
    <LoadingWrapper
      isLoading={isLoading || isSchemaMetadataLoading}
      loadingIndicatorClassName="text-green"
      loadingIndicatorSize="lg"
    >
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
        )}
      >
        <h1 className="custom-heading">Power Management</h1>
        <br />
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-5 gap-6">
            <div className="mb-4">
              <label className="block text-sm font-medium">Aux Gensets Maker and Model</label>
              <input
                type="text"
                name="auxGensetsMakerAndModel"
                value={form.auxGensetsMakerAndModel}
                onChange={handleInputChange}
                className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                  formErrors.auxGensetsMakerAndModel ? "border-red-600" : theme.uwhcAssistant.border
                }`}
              />
              {formErrors.auxGensetsMakerAndModel && (
                <p className="mt-1 text-xs text-red-600">{formErrors.auxGensetsMakerAndModel}</p>
              )}
            </div>
            <div className="mb-4">
              <label htmlFor="noOFAuxGensets" className="block text-sm font-medium">
                Number of Aux Gensets
              </label>
              <select
                id="noOFAuxGensets"
                name="noOFAuxGensets"
                value={form.noOFAuxGensets}
                onChange={handleInputChange}
                aria-required="true"
                className="mt-1 w-full rounded border p-2"
              >
                <option value="">Select number of gensets</option>
                {form.noOFAuxGensets && !TECH_FORM_GENSET_COUNTS.includes(String(form.noOFAuxGensets)) && (
                  <option value={form.noOFAuxGensets} disabled>
                    {form.noOFAuxGensets} — select 1 to 4
                  </option>
                )}
                {TECH_FORM_GENSET_COUNTS.map((count) => (
                  <option key={count} value={count}>
                    {count}
                  </option>
                ))}
              </select>
              {formErrors.noOFAuxGensets && <p className="mt-1 text-xs text-red-600">{formErrors.noOFAuxGensets}</p>}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium">Aux Gensets Power</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="auxGensetsPower"
                  value={form.auxGensetsPower}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${formErrors.auxGensetsPower ? "border-red-600" : theme.uwhcAssistant.border}`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                    formErrors.auxGensetsPower ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                >
                  KW
                </span>
              </div>
              <label className="block text-xs font-medium">Enter one power value in kW</label>
              {formErrors.auxGensetsPower && <p className="mt-1 text-xs text-red-600">{formErrors.auxGensetsPower}</p>}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">
                NOx Tier III compliance method (hpSCR / lpSCR / EGR etc.)
              </label>
              <input
                type="text"
                name="noxTierIIIComplianceMethod"
                value={form.noxTierIIIComplianceMethod}
                onChange={handleInputChange}
                className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                  formErrors.noxTierIIIComplianceMethod ? "border-red-600" : theme.uwhcAssistant.border
                }`}
              />
              <label className="block text-xs font-medium">As per the engine's NOx technical files and manual</label>
              {formErrors.noxTierIIIComplianceMethod && (
                <p className="mt-1 text-xs text-red-600">{formErrors.noxTierIIIComplianceMethod}</p>
              )}
            </div>

            <div className="mb-4" style={{ order: 10 }}>
              <label className="block text-sm font-medium">Shaft generator fitted?</label>
              <div className="mt-1">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="shaftGeneratorFitted"
                    value="yes"
                    checked={form.shaftGeneratorFitted === "yes"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="ml-4 inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="shaftGeneratorFitted"
                    value="no"
                    checked={form.shaftGeneratorFitted === "no"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              {formErrors.shaftGeneratorFitted && (
                <p className="mt-1 text-xs text-red-600">{formErrors.shaftGeneratorFitted}</p>
              )}
            </div>

            {form.shaftGeneratorFitted === "yes" && (
              <>
                <div className="mb-4" style={{ order: 11 }}>
                  <label className="block text-sm font-medium">Shaft Generator Rated Power</label>
                  <input
                    type="text"
                    name="shaftGeneratorRatedPower"
                    value={form.shaftGeneratorRatedPower}
                    onChange={handleInputChange}
                    className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                      formErrors.shaftGeneratorRatedPower ? "border-red-600" : theme.uwhcAssistant.border
                    }`}
                  />
                  {formErrors.shaftGeneratorRatedPower && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.shaftGeneratorRatedPower}</p>
                  )}
                </div>

                <div className="mb-4" style={{ order: 12 }}>
                  <label className="block text-sm font-medium">
                    Min Main Engine MCR% for onloading shaft generator
                  </label>
                  <input
                    type="text"
                    name="minMainEngineMcrForShaftGenerator"
                    value={form.minMainEngineMcrForShaftGenerator}
                    onChange={handleInputChange}
                    className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                      formErrors.minMainEngineMcrForShaftGenerator ? "border-red-600" : theme.uwhcAssistant.border
                    }`}
                  />
                  {formErrors.minMainEngineMcrForShaftGenerator && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.minMainEngineMcrForShaftGenerator}</p>
                  )}
                </div>
              </>
            )}
            <div className="mb-4" style={{ order: 20 }}>
              <label className="block text-sm font-medium">Aux boiler consumption @ 100% load</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="auxBoilerConsumption"
                  value={form.auxBoilerConsumption}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${formErrors.auxBoilerConsumption ? "border-red-600" : theme.uwhcAssistant.border}`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                    formErrors.auxBoilerConsumption ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                >
                  MT/hr
                </span>
              </div>
              {formErrors.auxBoilerConsumption && (
                <p className="mt-1 text-xs text-red-600">{formErrors.auxBoilerConsumption}</p>
              )}
            </div>

            <div className="mb-4" style={{ order: 21 }}>
              <label className="block text-sm font-medium">Maker, type, heating capacity</label>
              <input
                type="text"
                name="makerTypeHeatingCapacity"
                value={form.makerTypeHeatingCapacity}
                onChange={handleInputChange}
                className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${formErrors.makerTypeHeatingCapacity ? "border-red-600" : theme.uwhcAssistant.border}`}
              />
              {formErrors.makerTypeHeatingCapacity && (
                <p className="mt-1 text-xs text-red-600">{formErrors.makerTypeHeatingCapacity}</p>
              )}
            </div>
          </div>

          <div>
            <table className="w-50 text-left text-sm">
              <thead className="text-sm font-medium">
                <tr className="flex items-baseline">
                  <th scope="col" className="w-[218px] px-6 py-3">
                    Item
                  </th>
                  <th scope="col" className="w-[218px] px-6 py-3">
                    <label> No of items</label>
                  </th>
                  <th scope="col" className="w-[240px] px-6 py-3">
                    <label>Power Consumption</label>
                  </th>
                  <th scope="col" className="w-[215px] px-6 py-3">
                    <label>Type of power</label>
                  </th>
                  <th scope="col" className="px-6 py-3">
                    <label>Maker/model</label>
                  </th>
                </tr>
              </thead>
              <tbody>
                {table
                  .filter((item) => item.id !== 7 || scrubberFitted === "yes")
                  .map((item) => (
                    <tr key={item.id} className="flex items-baseline">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <input
                            type="text"
                            name="name"
                            value={item.name}
                            className={twMerge(
                              "mt-1 w-full rounded-md border p-2 focus:outline-none",
                              theme.uwhcAssistant.border,
                            )}
                            disabled
                          />
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center">
                          <input
                            type="text"
                            inputMode="numeric"
                            name={`tq8PowerManagementTable.${table.findIndex((row) => row.id === item.id)}.noOfItems`}
                            value={item.noOfItems}
                            onChange={(e) => updateTable(item.id, "noOfItems", e.target.value)}
                            className={tableInputClass(tableErrors[item.id]?.noOfItems)}
                          />
                        </div>
                        {tableError(tableErrors[item.id]?.noOfItems)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 font-medium">
                        <div className="relative flex items-center">
                          <input
                            type="text"
                            inputMode="decimal"
                            name={`tq8PowerManagementTable.${table.findIndex((row) => row.id === item.id)}.powerConsumptions`}
                            value={item.powerConsumptions}
                            onChange={(e) => updateTable(item.id, "powerConsumptions", e.target.value)}
                            className={tableInputClass(tableErrors[item.id]?.powerConsumptions)}
                          />
                          <span
                            className={twMerge(
                              "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                              theme.uwhcAssistant.border,
                              theme.balticQuestionnaire.grayBg,
                            )}
                          >
                            kW
                          </span>
                        </div>
                        {tableError(tableErrors[item.id]?.powerConsumptions)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <Select
                            classNamePrefix="rs"
                            options={options}
                            isSearchable={false}
                            placeholder="Select an option"
                            value={options.find((opt) => opt.value === item.typeOfPower)}
                            onChange={(selectedOption) =>
                              updateTable(item.id, "typeOfPower", selectedOption?.value || "")
                            }
                            styles={selectStyles}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <input
                            type="text"
                            name="makerModel"
                            value={item.makerModel}
                            onChange={(e) => updateTable(item.id, "makerModel", e.target.value)}
                            className={twMerge(
                              "mt-1 w-full rounded-md border p-2 focus:outline-none",
                              theme.uwhcAssistant.border,
                            )}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <br />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <div className="mb-4">
              <label className="block text-sm font-medium">Normal electrical load at sea</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="normalElectricalLoadAtSea"
                  pattern="[0-9]+"
                  value={form.normalElectricalLoadAtSea}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${formErrors.normalElectricalLoadAtSea ? "border-red-600" : theme.uwhcAssistant.border}`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                    formErrors.normalElectricalLoadAtSea ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                >
                  kW
                </span>
              </div>
              <label className="block text-xs font-medium">No ballast ops or hold washing </label>
              {formErrors.normalElectricalLoadAtSea && (
                <p className="mt-1 text-xs text-red-600">{formErrors.normalElectricalLoadAtSea}</p>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium">Normal electrical load in port/anchor</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="normalElectricalLoadinPortAtAnchor"
                  value={form.normalElectricalLoadinPortAtAnchor}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${formErrors.normalElectricalLoadinPortAtAnchor ? "border-red-600" : theme.uwhcAssistant.border}`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                    formErrors.normalElectricalLoadinPortAtAnchor ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                >
                  kW
                </span>
              </div>
              <label className="block text-xs font-medium">No ballast ops or hold washing </label>

              {formErrors.normalElectricalLoadinPortAtAnchor && (
                <p className="mt-1 text-xs text-red-600">{formErrors.normalElectricalLoadinPortAtAnchor}</p>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium">Usual working electrical load at sea</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="usualWorkingElectricalLoadatSea"
                  value={form.usualWorkingElectricalLoadatSea}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${formErrors.usualWorkingElectricalLoadatSea ? "border-red-600" : theme.uwhcAssistant.border}`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                    formErrors.usualWorkingElectricalLoadatSea ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                >
                  kW
                </span>
              </div>
              <label className="block text-xs font-medium">With ballast ops and/or hold washing </label>
              {formErrors.usualWorkingElectricalLoadatSea && (
                <p className="mt-1 text-xs text-red-600">{formErrors.usualWorkingElectricalLoadatSea}</p>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium">Usual working electrical load in port/anchor</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  placeholder="blockCoeficient"
                  name="usualWorkingLoadPortAtAnchor"
                  onChange={handleInputChange}
                  value={form.usualWorkingLoadPortAtAnchor}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${formErrors.usualWorkingLoadPortAtAnchor ? "border-red-600" : theme.uwhcAssistant.border}`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                    formErrors.usualWorkingLoadPortAtAnchor ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                >
                  kW
                </span>
              </div>
              <label className="block text-xs font-medium">Usual load with ballast ops and/or hold washing </label>
              {formErrors.usualWorkingLoadPortAtAnchor && (
                <p className="mt-1 text-xs text-red-600">{formErrors.usualWorkingLoadPortAtAnchor}</p>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium">Normal AE FO cons at sea</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  placeholder="blockCoeficient"
                  name="normalAEHFOConsumptionATSea"
                  pattern="[0-9]+"
                  onChange={handleInputChange}
                  value={form.normalAEHFOConsumptionATSea}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${formErrors.normalAEHFOConsumptionATSea ? "border-red-600" : theme.uwhcAssistant.border}`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                    formErrors.normalAEHFOConsumptionATSea ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                >
                  mt/d
                </span>
              </div>
              <label className="block text-xs font-medium">
                Normal daily consumption of vessel auxiliary engines at sea
              </label>
              {formErrors.normalAEHFOConsumptionATSea && (
                <p className="mt-1 text-xs text-red-600">{formErrors.normalAEHFOConsumptionATSea}</p>
              )}
            </div>
            <div className="mb-4">
              <label htmlFor="normalLoadAEFOConsumptionInPort" className="block text-sm font-medium">
                Normal AE FO cons in port
              </label>
              <div className="relative flex items-center">
                <input
                  id="normalLoadAEFOConsumptionInPort"
                  type="text"
                  inputMode="decimal"
                  name="normalLoadAEFOConsumptionInPort"
                  onChange={handleInputChange}
                  value={form.normalLoadAEFOConsumptionInPort}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${formErrors.normalLoadAEFOConsumptionInPort ? "border-red-600" : theme.uwhcAssistant.border}`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                    formErrors.normalLoadAEFOConsumptionInPort ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                >
                  mt/d
                </span>
              </div>
              <label className="block text-xs font-medium">Normal daily auxiliary-engine consumption in port</label>
              {formErrors.normalLoadAEFOConsumptionInPort && (
                <p className="mt-1 text-xs text-red-600">{formErrors.normalLoadAEFOConsumptionInPort}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-5 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium">Normal Aux Boiler FO cons at sea</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  placeholder="blockCoeficient"
                  name="mormalFuelConsumptionAuxiliaryBoilerAtSeal"
                  pattern="[0-9]+"
                  onChange={handleInputChange}
                  value={form.mormalFuelConsumptionAuxiliaryBoilerAtSeal}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${formErrors.mormalFuelConsumptionAuxiliaryBoilerAtSeal ? "border-red-600" : theme.uwhcAssistant.border}`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                    formErrors.mormalFuelConsumptionAuxiliaryBoilerAtSeal
                      ? "border-red-600"
                      : theme.uwhcAssistant.border,
                  )}
                >
                  mt/d
                </span>
              </div>
              <label className="block text-xs font-medium">If any, above min eco speed</label>
              {formErrors.mormalFuelConsumptionAuxiliaryBoilerAtSeal && (
                <p className="mt-1 text-xs text-red-600">{formErrors.mormalFuelConsumptionAuxiliaryBoilerAtSeal}</p>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium">Normal Aux Boiler FO cons in port</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  inputMode="decimal"
                  name="normalFuelConsumptionOfBoilerInPort"
                  value={form.normalFuelConsumptionOfBoilerInPort}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${formErrors.normalFuelConsumptionOfBoilerInPort ? "border-red-600" : theme.uwhcAssistant.border}`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                    formErrors.normalFuelConsumptionOfBoilerInPort ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                >
                  mt/d
                </span>
              </div>

              {formErrors.normalFuelConsumptionOfBoilerInPort && (
                <p className="mt-1 text-xs text-red-600">{formErrors.normalFuelConsumptionOfBoilerInPort}</p>
              )}
            </div>
          </div>
        </form>
      </div>
    </LoadingWrapper>
  );
};
